import type { SocialPost } from './types';
import type { ClubTheme } from './themes';

interface SystemFlairPostProps {
  post: SocialPost;
  theme: ClubTheme;
}

const TYPE_ICONS: Record<string, string> = {
  milestone: '🏅',
  highlight: '✦',
  announcement: '📢',
};

export default function SystemFlairPost({ post, theme }: SystemFlairPostProps) {
  const icon = TYPE_ICONS[post.post_type] || '✦';

  return (
    <article
      className="relative overflow-hidden rounded-2xl p-4"
      style={{
        background: `linear-gradient(135deg, ${theme.colors.primary}12, ${theme.colors.accent}08)`,
        border: `1px solid ${theme.colors.primary}25`,
      }}
    >
      {/* Decorative background icon */}
      <div
        className="pointer-events-none absolute -right-3 -top-3 text-6xl opacity-[0.06] select-none"
        aria-hidden="true"
      >
        {icon}
      </div>

      {/* Type badge */}
      <div
        className="mb-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest"
        style={{
          backgroundColor: `${theme.colors.primary}20`,
          color: theme.colors.primary,
        }}
      >
        <span>{icon}</span>
        <span>{post.post_type}</span>
      </div>

      {/* System avatar + name */}
      <div className="flex items-center gap-2.5">
        <div
          className="flex h-8 w-8 items-center justify-center rounded-full text-base"
          style={{
            background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.accent})`,
            color: theme.colors.bg,
          }}
        >
          {icon}
        </div>
        <span
          className="text-xs font-bold uppercase tracking-wider"
          style={{ color: theme.colors.primary }}
        >
          {theme.name}
        </span>
      </div>

      {/* Body */}
      {post.body && (
        <p
          className="mt-3 text-sm font-medium leading-relaxed"
          style={{ color: theme.colors.text }}
        >
          {post.body}
        </p>
      )}

      {/* Timestamp */}
      <p className="mt-2 text-[10px]" style={{ color: theme.colors.muted }}>
        {new Date(post.created_at).toLocaleString()}
      </p>
    </article>
  );
}
